import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import api from "../../services/api";

/**
 * Qué tickets de la bandeja pertenecen a un contacto que habla en otro sitio.
 *
 * 0424 F3 · CU-5. Desde que cada canal tiene su hilo, dos agentes pueden estar atendiendo
 * a la misma persona sin saberlo. La insignia de la fila es el primer aviso; el detalle
 * —quién y en qué canal— se pide al abrir la conversación.
 *
 * Se resuelve con **una consulta por página**, no una por fila: la bandeja se repinta con
 * cada mensaje entrante y un `N+1` ahí se nota enseguida.
 *
 * Va por contexto y no por props porque la fila está varios niveles por debajo de la lista
 * y atravesarla entera solo para una insignia ensucia cuatro componentes.
 */
const SiblingTicketsContext = createContext(new Set());

export const useSiblingTickets = () => useContext(SiblingTicketsContext);

export const SiblingTicketsProvider = ({ tickets, children }) => {
  const [conHermanos, setConHermanos] = useState(new Set());
  const ultimaClave = useRef("");

  // Solo los ids, ordenados: así la consulta no se repite cuando cambia el último
  // mensaje de un ticket pero la lista visible sigue siendo la misma.
  const clave = useMemo(
    () => (tickets || []).map(t => t?.id).filter(Boolean).sort((a, b) => a - b).join(","),
    [tickets]
  );

  useEffect(() => {
    if (!clave) {
      setConHermanos(new Set());
      ultimaClave.current = "";
      return undefined;
    }
    if (clave === ultimaClave.current) return undefined;
    ultimaClave.current = clave;

    let vigente = true;
    (async () => {
      try {
        const { data } = await api.get("/tickets/siblings-flags", { params: { ids: clave } });
        if (vigente) setConHermanos(new Set(data?.ticketIds || []));
      } catch {
        // Una insignia no puede romper la bandeja: si falla, no se pinta y ya.
        if (vigente) setConHermanos(new Set());
      }
    })();
    return () => { vigente = false; };
  }, [clave]);

  return (
    <SiblingTicketsContext.Provider value={conHermanos}>
      {children}
    </SiblingTicketsContext.Provider>
  );
};

SiblingTicketsProvider.propTypes = {
  tickets: PropTypes.array,
  children: PropTypes.node
};

export default SiblingTicketsContext;
